<template>
  <v-container class="new-client-page" fluid>
    <v-breadcrumbs :items="items" class="px-0 pt-0 pb-2" />
    <div class="new-client-header">
      <h1 class="new-client-header__title">{{ $route.meta.title }}</h1>
      <v-btn
        color="primary"
        outlined
        class="new-client-header__btn text-none"
        rounded
        @click.prevent="returnListClients()"
      >
        <v-icon left>mdi-arrow-left</v-icon>
        К списку клиентов
      </v-btn>
    </div>

    <v-card class="new-client-card mt-3" flat outlined>
      <div class="new-client-card__body pa-4">
        <v-form ref="form" v-model="valid" lazy-validation>
          <div class="new-client-section">
            <h2 class="new-client-section__title">
              <v-icon left small>mdi-account-plus-outline</v-icon>
              Основные данные
            </h2>
            <v-row class="new-client-form-row">
              <v-col cols="12" class="new-client-col">
                <v-checkbox
                  v-model="without_doorphone"
                  label="Без домофона (только шлагбаумы)"
                  hide-details
                  color="primary"
                  disabled
                />
              </v-col>
              <v-col cols="12" sm="6" class="new-client-col">
                <v-text-field
                  v-model="name"
                  label="Имя"
                  prepend-inner-icon="mdi-rename-box-outline"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="6" class="new-client-col">
                <v-text-field
                  v-model="email"
                  label="Email клиента"
                  prepend-inner-icon="mdi-email"
                  outlined
                  dense
                  hide-details
                  :rules="[v => !v || /.+@.+\..+/.test(v) || 'Введите корректный e-mail']"
                />
              </v-col>
              <v-col cols="12" sm="6" class="new-client-col">
                <v-text-field
                  v-model="telephone"
                  label="Телефон"
                  prepend-inner-icon="mdi-phone"
                  placeholder="79001234567"
                  :rules="[() => /^\d{11}$/.test(telephone) || 'Укажите 11 цифр номера телефона']"
                  outlined
                  dense
                  hide-details
                  maxlength="11"
                  counter="11"
                />
              </v-col>
              <v-col cols="12" sm="6" class="new-client-col">
                <v-text-field
                  v-model="password"
                  label="Пароль (необязательно)"
                  type="password"
                  prepend-inner-icon="mdi-lock-outline"
                  outlined
                  dense
                  hide-details
                  autocomplete="new-password"
                />
              </v-col>
              <v-col v-if="password" cols="12" sm="6" class="new-client-col">
                <v-text-field
                  v-model="password_confirmation"
                  label="Подтверждение пароля"
                  type="password"
                  prepend-inner-icon="mdi-lock-check-outline"
                  outlined
                  dense
                  hide-details
                  :rules="[v => !password || v === password || 'Пароли не совпадают']"
                  autocomplete="new-password"
                />
              </v-col>
            </v-row>
          </div>

          <template v-if="without_doorphone">
            <div class="new-client-section mt-4">
              <h2 class="new-client-section__title">
                <v-icon left small>mdi-gate</v-icon>
                Клиент без домофона
              </h2>
              <v-row class="new-client-form-row">
                <v-col cols="12" class="new-client-col">
                  <v-autocomplete
                    v-model="barrier_ids"
                    :items="barrierOptions"
                    :item-text="barrierDisplayText"
                    item-value="id"
                    label="Шлагбаумы (DS07P-LP)"
                    placeholder="Поиск по адресу или названию"
                    prepend-inner-icon="mdi-gate"
                    outlined
                    dense
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    hide-selected
                    clearable
                    :loading="barriersLoading"
                    :rules="withoutDoorphoneBarrierRules"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" class="new-client-col">
                  <VehicleNumberInput
                    :value="singleCarNumber"
                    label="Номер ТС"
                    :required="true"
                    @input="onCarNumberInput"
                  />
                </v-col>
              </v-row>
            </div>
          </template>

          <v-divider class="my-4" />

          <div class="new-client-actions">
            <v-btn
              color="primary"
              class="text-none"
              rounded
              :disabled="!valid || (without_doorphone && (barrier_ids.length === 0 || !(singleCarNumber || '').trim()))"
              @click="validate()"
            >
              <v-icon left>mdi-content-save-outline</v-icon>
              Сохранить
            </v-btn>
            <v-btn
              color="primary"
              outlined
              class="text-none"
              rounded
              @click.prevent="returnListClients()"
            >
              <v-icon left>mdi-close</v-icon>
              Отменить
            </v-btn>
          </div>
        </v-form>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';
import VehicleNumberInput from '@/components/VehicleNumberInput.vue';

export default {
  name: 'NewClient',
  components: { VehicleNumberInput },
  data () {
    return {
      valid: true,
      name: '',
      email: '',
      telephone: '',
      password: '',
      password_confirmation: '',
      without_doorphone: false,
      barrier_ids: [],
      singleCarNumber: '',
      barrierOptions: [],
      barriersLoading: false,
      items: [
        { text: 'Список клиентов', disabled: false, href: '/list-cliens' },
        { text: 'Добавление клиента', disabled: true, href: '#' },
      ],
    };
  },
  computed: {
    withoutDoorphoneBarrierRules () {
      if (!this.without_doorphone) return [];
      return [
        v => (Array.isArray(v) && v.length > 0) || 'Выберите хотя бы один шлагбаум',
      ];
    },
    barrierDisplayText () {
      return (barrier) => {
        if (!barrier) return '';
        const id = barrier.id != null ? barrier.id : '';
        const name = (barrier.name || '').trim();
        const addr = (barrier.address || '').trim();
        const parts = [`ID ${id}`];
        if (name) parts.push(name);
        if (addr) parts.push(addr);
        return parts.join(' — ') || `ID ${id}`;
      };
    },
  },
  watch: {
    without_doorphone (val) {
      if (val && this.barrierOptions.length === 0) this.loadBarriers();
    },
  },
  created () {
    if (this.without_doorphone) this.loadBarriers();
  },
  methods: {
    ...mapActions({
      newClient: 'Client/newClient',
      loadAllBarriers: 'Barriers/loadAllBarriers',
    }),
    loadBarriers () {
      this.barriersLoading = true;
      this.loadAllBarriers({ limit: 1000 })
        .then((res) => {
          const list = res.data || [];
          this.barrierOptions = list.filter(b =>
            (b.model || (b.device && b.device.model) || '').toString() === 'DS07P-LP',
          );
        })
        .catch(() => {
          this.$flash_warning('Не удалось загрузить список шлагбаумов');
          this.barrierOptions = [];
        })
        .finally(() => {
          this.barriersLoading = false;
        });
    },
    onCarNumberInput (value) {
      const filtered = (value || '').toString().replace(/[^0-9АВЕКМНОРСТУХA-Za-z]/gu, '').toUpperCase().slice(0, 20);
      this.singleCarNumber = filtered;
    },
    returnListClients () {
      this.$router.push({ name: 'ListClients' });
    },
    buildPayload () {
      const payload = {
        telephone: this.telephone.replace(/\D/g, '').slice(-11),
        name: this.name.trim() || undefined,
        email: this.email.trim() || undefined,
        without_doorphone: this.without_doorphone,
      };
      if (this.password) {
        payload.password = this.password;
        payload.password_confirmation = this.password_confirmation || this.password;
      }
      if (this.without_doorphone) {
        payload.barrier_ids = this.barrier_ids;
        const num = (this.singleCarNumber || '').trim();
        payload.car_numbers = num ? [num] : undefined;
      }
      return payload;
    },
    validate () {
      this.$refs.form.validate();
      this.$nextTick(() => {
        if (!this.valid) {
          this.$flash_warning('Заполните обязательные поля');
          return;
        }
        if (this.without_doorphone && (this.barrier_ids.length === 0 || !(this.singleCarNumber || '').trim())) {
          this.$flash_warning('Для клиента без домофона укажите шлагбаумы и хотя бы один гос. номер');
          return;
        }
        const payload = this.buildPayload();
        this.newClient(payload)
          .then(() => {
            this.$router.push({ name: 'ListClients' });
            this.$flash_success('Клиент создан успешно');
          })
          .catch((err) => {
            const data = err.response && err.response.data;
            if (data && typeof data === 'object') {
              Object.entries(data).forEach(([field, messages]) => {
                const list = Array.isArray(messages) ? messages : [messages];
                list.forEach(m => this.$flash_warning(`${field}: ${m}`));
              });
            } else {
              this.$flash_warning('Ошибка при создании клиента');
            }
          });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.new-client-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 0;
}

.new-client-header__title {
  font-size: 1.5rem;
  font-weight: 400;
  color: #37474f;
  margin: 0;
  letter-spacing: -0.02em;
}

.new-client-header__btn {
  text-transform: none;
  font-weight: 500;
}

.new-client-card {
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.new-client-card__body {
  max-width: 720px;
}

.new-client-section {
  margin-bottom: 0;
}

.new-client-section__title {
  font-size: 1rem;
  font-weight: 500;
  color: #37474f;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
}

.new-client-form-row {
  margin: 0 -12px;
}

.new-client-col {
  padding-top: 0;
  padding-bottom: 12px;
}

.new-client-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

@media screen and (max-width: 600px) {
  .new-client-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .new-client-header__title {
    font-size: 1.4rem;
  }
}
</style>
